import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import styled from 'styled-components';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import StyledLoading from '../../app/components/loading/StyledLoading';
import StyledButton from '../../app/components/styledComponents/StyledButton';
import SeoHead from '../../app/components/SeoHead';
import { formatDateTime, timeAgo } from '../../utils/helper';

const FacebookIcon = () => {
  return (
    <svg
      width="35"
      height="35"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 448 512"
    >
      <path d="M64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64h98.2V334.2H109.4V256h52.8V222.3c0-87.1 39.4-127.5 125-127.5c16.2 0 44.2 3.2 55.7 6.4V172c-6-.6-16.5-1-29.6-1c-42 0-58.2 15.9-58.2 57.2V256h83.6l-14.4 78.2H255V480H384c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64z" />
    </svg>
  );
};

export const StyledTooltip = styled(Tooltip)`
  position: absolute !important;
  text-overflow: ellipsis;
  white-space: nowrap;
  .tooltip-arrow {
    // display: none !important;
  }

  .tooltip-inner {
    background-color: ${({ theme }) => theme?.dark40} !important;
    border-radius: 2px;
    padding: 4px;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.06), 0 8px 16px 0 rgba(0, 0, 0, 0.1);
    font-size: 12px;
    font-family: ${({ theme }) => theme?.fontFamily} !important;
    overflow: visible;
    max-width: 300px;
  }
`;

const Card = styled.div`
  border-radius: 5px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
`;

const Social = () => {
  const [facebookData, setFacebookData] = useState({});
  const [allPosts, setAllPosts] = useState([]);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [afterCursor, setAfterCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadMore, setLoadMore] = useState(false);

  useEffect(() => {
    fetchData(16);
  }, []);

  const fetchData = async (limit) => {
    setLoading(true);
    const facebookURL = `https://graph.facebook.com/v20.0/${process.env.REACT_APP_FACEBOOK_PAGE_ID}?fields=id,name,about,description,fan_count,link,posts.limit(${limit}){message,created_time,attachments{media},likes.summary(true),comments.summary(true),permalink_url},followers_count,picture.type(large)&access_token=${process.env.REACT_APP_FACEBOOK_ACCESS_TOKEN}`;

    try {
      const fetchFacebookContent = await axios.get(facebookURL);
      setFacebookData(fetchFacebookContent.data);
      setAfterCursor(fetchFacebookContent?.data?.posts?.paging?.next);
      const facebookContent = fetchFacebookContent.data.posts.data.map(
        (post) => {
          return {
            id: post.id,
            message: post.message,
            image: post.attachments ? post.attachments.data : null,
            likes: post.likes.summary.total_count,
            comments: post.comments.summary.total_count,
            facebook: true,
            permalink_url: post.permalink_url,
            created_time: post.created_time,
          };
        },
      );
      setAllPosts((prevPosts) => [...prevPosts, ...facebookContent]);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const loadMorePosts = async () => {
    if (!afterCursor) {
      return;
    }
    setLoadMore(true);
    const response = await axios.get(afterCursor);
    if (!response?.data?.paging?.next) {
      setHasMorePosts(false);
    } else {
      setAfterCursor(response?.data?.paging?.next);
    }
    const facebookContent = response?.data?.data?.map((post) => {
      return {
        id: post?.id,
        message: post.message,
        image: post.attachments ? post.attachments.data : null,
        likes: post.likes.summary.total_count,
        comments: post.comments.summary.total_count,
        permalink_url: post.permalink_url,
        created_time: post.created_time,
      };
    });
    setAllPosts((prevPosts) => [...prevPosts, ...facebookContent]);
    setLoadMore(false);
  };

  const PostContent = ({ item }) => {
    const [showReadMore, setShowReadMore] = useState(false);
    const contentRef = useRef(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
      const checkOverflow = () => {
        if (contentRef.current) {
          const content = contentRef.current;
          // Check if content overflows its container
          setShowReadMore(content.scrollHeight > content.clientHeight);
        }
      };

      // Run on mount and when item.message changes
      checkOverflow();

      // Optionally, re-check on window resize
      window.addEventListener('resize', checkOverflow);
      return () => {
        window.removeEventListener('resize', checkOverflow);
      };
    }, [item.message]);

    const toggleReadMore = () => {
      setExpanded(!expanded);
    };

    return (
      <div>
        <p
          ref={contentRef}
          style={{
            maxHeight: expanded ? '5000px' : '100px',
            overflow: 'hidden',
          }}
        >
          {item.message}
        </p>
        {showReadMore && (
          <span onClick={toggleReadMore}>
            <p style={{ cursor: 'pointer', color: '#9a9a9a' }}>
              {expanded ? 'Read Less' : 'Read More'}
            </p>
          </span>
        )}
      </div>
    );
  };

  const seoData = {
    title: 'Follow BoostFloral on Facebook, Instagram, and YouTube',
    description:
      'Follow BoostFloral and keep up to date on the latest discounts, feature releases, and more.',
    keywords:
      'Flower Shop Websites, Floral Web Design Software, Floral CMS, Building a Floral Website, Floral POS, Floral point of sale, Floral website builder, Build a Floral Website, Florist website design, Floral website design, best floral website templates, Florist web templates, Create a flower shop website, Floral e-commerce website, Best website platform for florists, Flower shop online store, Florist online ordering system, Flower delivery website, POS system for florists, Florist website SEO, Customizable florist website, Floral event booking website, the best website for retail flower shops',
    ogTitle: 'Follow BoostFloral on Facebook, Instagram, and YouTube',
    ogDescription:
      'Follow BoostFloral and keep up to date on the latest discounts, feature releases, and more.',
    ogType: 'company',
    ogSiteName: 'BoostFloral',
    ogImage: 'https://www.boostfloral.com/logo.svg',
  };

  return (
    <>
      <SeoHead data={seoData} />
      <div className="py-4">
        <div className="d-flex flex-column justify-content-center align-items-center mt-4">
          <h3
            style={{
              fontSize: '28px',
              fontWeight: '100',
              textTransform: 'uppercase',
              textAlign: 'center',
            }}
          >
            Follow us on social media
          </h3>
        </div>
        {loading ? (
          <div>
            <StyledLoading />
          </div>
        ) : (
          <div>
            <div className="d-flex flex-column justify-content-center align-items-center mb-2">
              <a
                href={facebookData?.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ cursor: 'pointer' }}
              >
                <h3
                  style={{
                    fontSize: '50px',
                    letterSpacing: '4px',
                    color: '#CB1D8D',
                  }}
                >
                  {`@${facebookData?.name}`}
                </h3>
              </a>
              <div>
                <span
                  style={{ fontSize: '18px' }}
                  className="mx-2"
                >{`${facebookData?.fan_count} likes`}</span>
                <span
                  style={{ border: '1px solid #535870', borderRadius: '10px' }}
                ></span>
                <span
                  style={{ fontSize: '18px' }}
                  className="mx-2"
                >{`${facebookData?.followers_count} Followers`}</span>
              </div>
            </div>
            <ResponsiveMasonry
              className="p-4"
              columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3, 1000: 4 }}
            >
              <Masonry gutter="25px">
                {allPosts.map((item) => (
                  <Card style={{ backgroundColor: '#fff' }} key={item.id}>
                    {item?.image && (
                      <div
                        style={{
                          borderTopRightRadius: '5px',
                          borderTopLeftRadius: '5px',
                        }}
                      >
                        <a
                          href={item?.permalink_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ cursor: 'pointer' }}
                        >
                          <img
                            style={{
                              borderTopRightRadius: '5px',
                              borderTopLeftRadius: '5px',
                              width: '100%',
                            }}
                            src={item?.image[0]?.media?.image?.src}
                          />
                        </a>
                      </div>
                    )}
                    <div>
                      <div className="p-3">
                        <PostContent item={item} />
                      </div>
                      <div className="d-flex flex-row justify-content-between px-3">
                        <span className="d-flex flex-row">
                          <p>{item?.likes}</p>
                          <p className="mx-1">
                            {item?.likes <= 1 ? 'Like' : 'Likes'}
                          </p>
                        </span>
                        <span className="d-flex flex-row">
                          <p>{item?.comments}</p>
                          <p className="mx-1">
                            {item?.comments <= 1 ? 'Comment' : 'Comments'}
                          </p>
                        </span>
                      </div>
                      <hr style={{ marginTop: '1px' }} />
                      <div className="px-3 d-flex flex-row justify-content-between align-items-center mb-3">
                        <div className="d-flex flex-row align-items-center">
                          <div>
                            <a
                              style={{ cursor: 'pointer' }}
                              href={facebookData?.link}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                style={{ height: '40px', width: '40px' }}
                                src={facebookData?.picture?.data?.url}
                              />
                            </a>
                          </div>
                          <div className="mx-2">
                            <a
                              href={facebookData?.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ cursor: 'pointer' }}
                              onMouseOver={(e) =>
                                (e.currentTarget.style.textDecoration =
                                  'underline')
                              }
                              onMouseOut={(e) =>
                                (e.currentTarget.style.textDecoration = 'none')
                              }
                            >
                              {facebookData?.name}
                            </a>
                            <OverlayTrigger
                              overlay={
                                <StyledTooltip>
                                  {formatDateTime(item.created_time)}
                                </StyledTooltip>
                              }
                            >
                              <p
                                className="text-muted"
                                style={{
                                  width: 'fit-content',
                                }}
                              >
                                {timeAgo(item.created_time)}
                              </p>
                            </OverlayTrigger>
                          </div>
                        </div>
                        <div>
                          <span>
                            <a
                              href={facebookData?.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ cursor: 'pointer' }}
                            >
                              <FacebookIcon />
                            </a>
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </Masonry>
              <div className="d-flex justify-content-center py-4">
                <button
                  disabled={loadMore || !hasMorePosts}
                  onClick={loadMorePosts}
                  style={{
                    border: '1px solid #CB1D8D',
                    backgroundColor: '#CB1D8D',
                    color: 'white',
                    borderRadius: '2px',
                    cursor: !hasMorePosts ? 'not-allowed' : 'pointer',
                    opacity: !hasMorePosts && '0.5',
                  }}
                  className="py-2 px-4"
                >
                  <span style={{ fontSize: '18px', fontWeight: '400' }}>
                    {loadMore ? 'Loading...' : 'LOAD MORE'}
                  </span>
                </button>
              </div>
            </ResponsiveMasonry>
          </div>
        )}
      </div>
    </>
  );
};

export default Social;
